import { useState, useContext } from 'react';
import {
  HeaderLogo,
  ContactUsLink,
  HamburgerMenuLink,
  FooterDisclaimer,
} from 'organisms';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  ANALYTICS_MESSAGES,
  SCREEN_NAMES,
} from 'common/utils/constants/AnalyticsConstants';
import { setMaxDate, validateDatePickerInput, minYears } from 'common/utils';
import {
  getMaxLength,
  NavigateConst,
  getAnalyticsBaseConfig,
  getErrorTypeByCode,
  domainForSegment,
  memberIdRegFormat,
  convertStringToDate,
  convertStrDateFormat,
  specialCharacterFormat,
  DeviceTypes,
  useDeviceType,
  DateFormatRegex,
  EmailRegex,
  useAnalytics,
} from 'common';
import { GlobalContext, isAvMed } from 'context';
import {
  Label,
  ErrorMessage,
  Button,
  CheckIconBig,
  Headings,
  InputField,
  GlobalError,
  Loader,
  Link,
  Paragraph,
  Checkbox,
  HelpIcon,
  ModalOverlay,
  DatePicker,
} from '@sentara-health/optimahealth_web';
import { useRequestUsername } from '@sentara-health/sentara-api-hooks-core';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { AdobeFormConfig } from 'common/AdobeFormConfig';
import shpMemberIdCardImg from '../../assets/images/memberidcard_img.png';
import avmedMemberIdCardImg from '../../assets/images/avmedmemberid_img.png';

const ForgotUserName = () => {
  const { logging } = useContext(GlobalContext);
  const { t } = useTranslation();
  // State variables
  const [loader, setLoader] = useState(false);
  const [isMemberIdHelpOpen, setIsMemberIdHelpOpen] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [isCheckedPersonal, setIsCheckedPersonal] = useState(true);
  const [isCheckedMemberId, setIsCheckedMemberId] = useState(false);
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const [dobErrorMessages, setDobErrorMessages] = useState<string>('');

  // Custom hooks for API calls and analytics
  const { requestUsername } = useRequestUsername();

  const {
    formSubmissionSucceeded,
    formSubmissionFailed,
    pageView,
  } = useAdobeAnalytics();

  // Form handling using react-hook-form
  const {
    handleSubmit,
    register,
    unregister,
    setValue,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<InputFieldProps>({
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      personalEmail: '',
      dob: '',
      memberId: '',
    },
  });
  // Navigation hooks
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { trackContentListing } = useAnalytics();

  // Device type detection
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;

  // Define form fields based on the selected option
  const formData: Record<string, any> = isCheckedPersonal
    ? {
        firstName: {
          id: 'firstName',
          required: true,
          placeholder: t('activation.firstNamePlaceholder'),
          maxLength: getMaxLength.name,
          label: t('activation.firstNameLabel'),
          error: errors.firstName,
          displayError: t('activation.firstNameError'),
          restrictInputValue: specialCharacterFormat,
        },
        lastName: {
          id: 'lastName',
          required: true,
          placeholder: t('activation.lastNamePlaceholder'),
          maxLength: getMaxLength.name,
          label: t('activation.lastNameLabel'),
          error: errors.lastName,
          displayError: t('activation.lastNameError'),
          restrictInputValue: specialCharacterFormat,
        },
        personalEmail: {
          id: 'personalEmail',
          required: true,
          placeholder: t('common.emaillblPlaceholder'),
          maxLength: getMaxLength.email,
          label: t('common.emailAddress'),
          error: errors.personalEmail,
          displayError: t('common.emailIdValid'),
          restrictInputValue: EmailRegex,
        },
        dob: {
          id: 'dob',
          required: true,
          placeholder: t('common.dateOfBirthPlaceholder'),
          maxLength: getMaxLength.dob,
          label: t('common.dateOfBirth'),
          error: errors.dob,
          displayError: dobErrorMessages,
          restrictInputValue: DateFormatRegex,
        },
      }
    : {
        memberId: {
          id: 'memberId',
          required: true,
          showIcons: true,
          icon: <HelpIcon />,
          iconActionOpen: () => setIsMemberIdHelpOpen(true),
          iconActionClose: () => setIsMemberIdHelpOpen(false),
          placeholder: t('common.enterMemberID'),
          maxLength: getMaxLength.memberId,
          label: t('common.memberId'),
          error: errors.memberId,
          displayError: t('common.memberIdRequired'),
          restrictInputValue: memberIdRegFormat,
        },
      };

  // Input field properties interface
  interface InputFieldProps {
    firstName: string;
    lastName: string;
    personalEmail: string;
    dob: string;
    memberId: string;
    guest: string;
  }

  // Image for member ID tooltip Modal
  let memberIDImg = isAvMed() ? avmedMemberIdCardImg : shpMemberIdCardImg;

  /* oncancel click navigate to sign in */
  const cancelClick = () => {
    formSubmissionFailed({
      segment: `${domainForSegment}${pathname}`,
      form: AdobeFormConfig['forgetUsernameStep1Cancel'],
    });
    logging?.trace?.({
      message: ANALYTICS_MESSAGES.FORGOT_USERNAME_CANCEL,
      screenName: SCREEN_NAMES.FORGOT_USERNAME,
      reason: ANALYTICS_MESSAGES.USER_CLICKED_CANCEL,
    });
    navigate(NavigateConst.signIn);
  };

  // Function to handle form submission
  const onSubmit = async (formData: InputFieldProps) => {
    setLoader(true);
    trackContentListing(
      '502',
      ANALYTICS_MESSAGES.FORGOT_USERNAME_SAVE_CLICK,
      2
    );

    // Convert and format date of birth if personal info option is selected
    const dateString = convertStringToDate(formData.dob);
    const formattedDate = convertStrDateFormat(dateString, true);

    // Prepare payload based on selected option
    const payload = isCheckedPersonal
      ? {
          firstName: formData.firstName,
          lastName: formData.lastName,
          personalEmail: formData.personalEmail,
          dob: formattedDate,
        }
      : {
          memberId: formData.memberId,
        };

    let response: any;
    setErrorMessage(false);
    response = await requestUsername(payload);
    if (response?.error) {
      setErrorMessage(true);
      if (isCheckedMemberId) {
        trackContentListing(
          '511',
          ANALYTICS_MESSAGES.POP_MEMBER_ID_NOT_RECOGNIZED,
          1
        );
      }
      logging?.error({
        additionalDetails: getAnalyticsBaseConfig(),
        apiDetails: {
          exception: {
            errorType: getErrorTypeByCode(response?.errorCode?.toString()),
            message: response?.errorDetails ?? response?.error,
            stack: response?.error,
          },
          response: { httpStatus: response?.errorCode },
        },
        message: ANALYTICS_MESSAGES.FORGOT_USERNAME_EXCEPTION,
        screenName: SCREEN_NAMES.FORGOT_USERNAME,
      });
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['forgetUsernameStep1Error'],
          formFailReason: response?.errorDetails ?? response?.error,
        },
      });
      trackContentListing(
        '504',
        ANALYTICS_MESSAGES.FORGOT_USERNAME_SAVE_FAILURE,
        4
      );
    } else {
      logging?.trace({
        additionalDetails: getAnalyticsBaseConfig(),
        message: ANALYTICS_MESSAGES.FORGOT_USERNAME_SUCCESS,
        screenName: SCREEN_NAMES.FORGOT_USERNAME,
      });
      formSubmissionSucceeded({
        segment: `${domainForSegment}${pathname}`,
        form: AdobeFormConfig['forgetUsernameStep1Success'],
      });
      pageView({
        segment: `${domainForSegment}${pathname}`,
        page: {
          eventType: 'pageView',
          pageName: 'Forgot Username Success',
          pageType: 'Authentication',
          siteSection: 'forgot-username',
        },
      });
    }
    setResponse(response);
    setLoader(false);
    return response;
  };

  // Extract the nested ternary into a variable for clarity and to avoid compile errors
  let mainContent;
  if (response?.status === 200) {
    mainContent = (
      <>
        {/* success content */}
        <div className="row d-flex justify-content-center mt-4 pt-2">
          <div className="col-lg-7 col-12 text-center">
            <CheckIconBig className="modal-icon" />
            <Headings
              level={1}
              text={t('common.activationSuccess')}
              variant="semiBold"
              data-testid="successMessageHeading"
              className="my-4 pb-1"
            />
            <Paragraph
              text={
                <Trans
                  i18nKey="activation.successMessage"
                  values={{ phone: t('activation.successMessageNo') }}
                  components={{
                    link1: (
                      <Link
                        to={`tel:+${t('activation.successMessageNo')}`}
                        className="text-decoration-underline"
                        data-testid="phone-link"
                      />
                    ),
                  }}
                />
              }
              className="lineBrake lh-sm"
              data-testid="SuccessMessage"
            />
            <div className="py-3 my-4">
              <Button
                onClick={() => navigate(NavigateConst.signIn)}
                data-testid={'returnToSignInButton'}
                type="submit"
                variant="primary_btn"
                className="btn-md-width"
              >
                {t('common.returnLogin')}
              </Button>
            </div>
          </div>
        </div>
        {/* success content */}
      </>
    );
  } else {
    mainContent = (
      <div className="row">
        <div className="col-12">
          {loader && <Loader />}
          <div className="row">
            <div className={`col-xl-6 col-sm-9 col-12 px-sm-5 px-0`}>
              {errorMessage && (
                <div className="row">
                  <div className="col-12 mt-4">
                    <GlobalError data-testid="globalError">
                      {t('common.serviceErrorRequestId')}
                    </GlobalError>
                  </div>
                </div>
              )}
              <form
                onSubmit={handleSubmit(onSubmit)}
                data-testid="forgotUsernameForm"
              >
                <Headings
                  level={1}
                  text={t('common.requestUserName')}
                  variant="semiBold"
                  className="my-4 pb-4"
                  data-testid="forgotUsernameHeading"
                />
                <div className="row">
                  <Label data-testid="radioBtnHeading" className="mb-3">
                    {t('forgotUsername.labelRadioBtnHeader')}
                  </Label>
                  <div className="row">
                    <div className="col-md-6 col-12 mb-4">
                      <Checkbox
                        type="radio"
                        id="personalInfo"
                        data-testid="personalInfoRadioBtn"
                        checked={isCheckedPersonal}
                        onChange={() => {
                          setIsCheckedPersonal(true);
                          setIsCheckedMemberId(false);
                          setErrorMessage(false);
                          Object.keys(formData).forEach((key) => {
                            setValue(formData[key].id, '');
                            unregister(formData[key].id);
                            clearErrors(formData[key].id);
                          });
                        }}
                      >
                        {t('common.personalInfo')}
                      </Checkbox>
                    </div>
                    <div className="col-md-6 col-12 mb-4 d-flex">
                      <Checkbox
                        type="radio"
                        id="memberId"
                        data-testid="memberIdRadioBtn"
                        checked={isCheckedMemberId}
                        onChange={() => {
                          setIsCheckedMemberId(true);
                          setIsCheckedPersonal(false);
                          setErrorMessage(false);
                          trackContentListing(
                            '508',
                            ANALYTICS_MESSAGES.FORGOT_USERNAME_MEMBER_ID_SAVE,
                            7
                          );
                          Object.keys(formData).forEach((key) => {
                            setValue(formData[key].id, '');
                            unregister(formData[key].id);
                            clearErrors(formData[key].id);
                          });
                        }}
                      >
                        {t('common.memberId')}
                      </Checkbox>
                    </div>
                  </div>
                </div>
                <div className="row mb-4">
                  {formData &&
                    Object.keys(formData).map((key) => {
                      const field = formData[key];
                      return (
                        <div className="col-12" key={field.id}>
                          <div className="mb-3">
                            <Label
                              htmlFor={field.id}
                              variant="formLabel"
                              className="mb-1"
                              data-testid={`${field.id}Label`}
                            >
                              {field.label}
                            </Label>
                            <div className="d-flex align-items-center">
                              {field.id === 'dob' && isCheckedPersonal ? (
                                <div className="flex-1">
                                  <DatePicker
                                    selectedValue={field.value}
                                    maxLength={field.maxLength}
                                    fieldName={field.id}
                                    maxDate={setMaxDate(minYears)}
                                    data-testid={`${field.id}Input`}
                                    placeholder={field.placeholder}
                                    updateValue={(value: string) => {
                                      setValue(field.id, value, {
                                        shouldValidate: true,
                                      });
                                      clearErrors(field.id);
                                    }}
                                    errorClass={
                                      field.error ? 'error-border' : ''
                                    }
                                    {...register(field.id, {
                                      required: field.required,
                                      validate: {
                                        dateFormat: (value: string) => {
                                          if (
                                            field.restrictInputValue.test(value)
                                          ) {
                                            setDobErrorMessages('');
                                            return true;
                                          }
                                          const dateFormatError = String(
                                            t(
                                              'forgotUsername.dateofbirthFormat'
                                            ) ?? ''
                                          );
                                          setDobErrorMessages(dateFormatError);
                                          return dateFormatError;
                                        },
                                        minAge: (value: string) => {
                                          const isMinAgeValid =
                                            validateDatePickerInput(
                                              value,
                                              minYears
                                            );
                                          if (isMinAgeValid) {
                                            setDobErrorMessages('');
                                            return true;
                                          }
                                          const minAgeError = String(
                                            t('forgotUsername.lessAge', {
                                              minYears: minYears,
                                            }) ?? ''
                                          );
                                          setDobErrorMessages(minAgeError);
                                          return minAgeError;
                                        },
                                      },
                                    })}
                                  />
                                </div>
                              ) : (
                                <InputField
                                  id={field.id}
                                  type="text"
                                  placeholder={field.placeholder}
                                  maxLength={field.maxLength}
                                  data-testid={`${field.id}Input`}
                                  error={field.error}
                                  className={field.error ? 'error-border' : ''}
                                  {...register(field.id, {
                                    required: field.required,
                                    onChange: (e) => {
                                      clearErrors(field.id);
                                      setValue(field.id, e.target.value);
                                    },
                                    pattern: field.restrictInputValue,
                                    validate: field.id
                                      ? (value) => {
                                          return field.restrictInputValue.test(
                                            value
                                          );
                                        }
                                      : undefined,
                                  })}
                                />
                              )}
                              {field.showIcons && field.icon && (
                                <Button
                                  onClick={field.iconActionOpen}
                                  className="ms-2 cursor-pointer"
                                  variant="icon_btn"
                                  data-testid={`${field.id}HelpIcon`}
                                >
                                  {field.icon}
                                </Button>
                              )}
                            </div>
                            {field.error && (
                              <ErrorMessage data-testid={`${field.id}Error`}>
                                {field.displayError || t('common.dobRequired')}
                              </ErrorMessage>
                            )}
                            <ModalOverlay
                              isOpen={isMemberIdHelpOpen}
                              onClose={() => setIsMemberIdHelpOpen(false)}
                            >
                              <div className="modal-header gap-2">
                                {isMobile ? (
                                  <Paragraph
                                    level={3}
                                    text={t('common.memberIDCardToolTipHeader')}
                                    className="modal-title"
                                    variant="medium"
                                  />
                                ) : (
                                  <Headings
                                    text={t('common.memberIDCardToolTipHeader')}
                                    data-testid="memIDCardToolTipHeader"
                                    className="modal-title"
                                    variant="semiBold"
                                  />
                                )}
                              </div>
                              <div className="modal-body pt-4 gap-2">
                                <Paragraph
                                  text={t('common.memberIDCardToolTipText1')}
                                  variant="regular"
                                  data-testid="memIDCardToolTipDescOne"
                                />
                                <div className="pb-3">
                                  <img
                                    src={memberIDImg}
                                    alt="Member ID Card"
                                    className="img-fluid"
                                  />
                                </div>
                                <Paragraph
                                  text={t('common.memberIDCardToolTipText4')}
                                  variant="regular"
                                  data-testid="memIDCardToolTipDescTwo"
                                  className="mb-0"
                                />
                              </div>
                            </ModalOverlay>
                          </div>
                        </div>
                      );
                    })}
                </div>
                <div className="row pt-2">
                  <div className="col-md-auto col-12 d-grid gap-2 d-md-block mb-4">
                    <Button
                      onClick={() => {}}
                      data-testid={'submitButton'}
                      type="submit"
                      disabled={loader}
                      variant="primary_btn"
                      className={`btn-md-width ${!isValid ? 'disabled' : ''}`}
                    >
                      {t('common.submit')}
                    </Button>
                  </div>
                  <div className="col-md-auto col-12 d-grid gap-2 d-md-block mb-4">
                    <Button
                      onClick={cancelClick}
                      data-testid={'cancelButton'}
                      type="button"
                      variant="secondary_btn"
                      className="btn-md-width"
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column h-100">
      {/* Header section */}
      <header className="border-bottom">
        {/* Container for the header */}
        <div className="container-fluid">
          {/* Navigation bar */}
          <nav className="header container-lg d-flex align-items-center justify-content-between">
            <HeaderLogo />
            <div className="d-flex flex-no-wrap align-items-center">
              {/* Contact us link, visible on extra-large and large screens */}
              <div className="d-none d-xl-block d-lg-block d-md-none">
                <ContactUsLink showIcon={false} />
              </div>
              {/* Hamburger menu link, visible on large, medium, and small screens */}
              <div className="d-lg-none d-md-block d-sm-block">
                <HamburgerMenuLink showLogout={false} />
              </div>
            </div>
          </nav>
        </div>
      </header>
      {/* Main content container */}
      <div className="container-fluid">
        <div id="main-content" className="container-lg px-main">
          {/* Reset password component */}
          {mainContent}
        </div>
      </div>
      {/* Footer section */}
      <footer className="container-lg mt-auto">
        <hr className="mt-3 mb-4 pt-2" />
        {/* Component to display the footer disclaimer */}
        <FooterDisclaimer />
      </footer>
    </div>
  );
};

export default ForgotUserName;
